/**
 * 🚀 UNIVERSAL FOOTER ELEMENT 2025
 * 
 * Dynamisches Footer-Element für HeroHeader
 * - Logo mit Upload
 * - Slogan Text
 * - Social Media Links
 * - Navigation Links
 * - Verschiedene Layout-Templates
 * - Voll responsive (Desktop/Mobile)
 * - Floating Toolbar für alle Settings
 */

import React, { useState, useRef } from 'react';
import FooterFloatingToolbar from './FooterFloatingToolbar';
import { useDebouncedCallback } from '../../hooks/useDebounce';
import './UniversalFooterElement.css';

function appendPx(value) {
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    return `${value}px`;
}

function UniversalFooterElement({
    element,
    isEditMode,
    isMobileLayout,
    onUpdate,
    onSelect,
    colors = {},
    fonts = {},
    isCurrentlyEditing = false,
    onEditingStart = () => {},
    onEditingEnd = () => {}
}) {
    const [showToolbar, setShowToolbar] = useState(false);
    const footerRef = useRef(null);

    // ⚡ OPTIMISTIC UI: Lokaler State für Slider-Werte (SOFORTIGES Feedback)
    const [localStyles, setLocalStyles] = useState({
        paddingTop: element.footerPaddingTop,
        paddingBottom: element.footerPaddingBottom,
        paddingLeft: element.footerPaddingLeft,
        paddingRight: element.footerPaddingRight,
        paddingTopMobile: element.footerPaddingTopMobile,
        paddingBottomMobile: element.footerPaddingBottomMobile,
        paddingLeftMobile: element.footerPaddingLeftMobile,
        paddingRightMobile: element.footerPaddingRightMobile,
        logoWidth: element.logoWidth,
        logoWidthMobile: element.logoWidthMobile,
        sloganFontSize: element.sloganFontSize,
        sloganFontSizeMobile: element.sloganFontSizeMobile,
        linkFontSize: element.linkFontSize,
        linkFontSizeMobile: element.linkFontSizeMobile,
        socialIconSize: element.socialIconSize,
        socialIconSizeMobile: element.socialIconSizeMobile,
        gap: element.footerGap,
        gapMobile: element.footerGapMobile,
        borderTopWidth: element.borderTopWidth,
        socialIconContainerPadding: element.socialIconContainerPadding,
        socialIconContainerBorderWidth: element.socialIconContainerBorderWidth
    });

    // ⚡ Debounced Parent Update (300ms für Firebase)
    const debouncedParentUpdate = useDebouncedCallback((updates) => {
        if (onUpdate) {
            onUpdate(updates);
        }
    }, 300);

    // ✅ Click Handler - Toggle Toolbar
    const handleClick = (e) => {
        if (!isEditMode) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        if (isCurrentlyEditing) {
            // ✅ TOGGLE: Schließe Toolbar wenn bereits geöffnet
            handleToolbarClose();
        } else {
            // ✅ Öffne Toolbar (schließt automatisch andere Toolbars via onEditingStart)
            onEditingStart();
            setShowToolbar(true);
            if (onSelect) onSelect();
        }
    };

    const handleToolbarClose = () => {
        setShowToolbar(false);
        onEditingEnd();
    };

    const handleStyleChange = (updates) => {
        console.log('🎨 UniversalFooterElement handleStyleChange:', updates);
        
        // ✅ Liste aller Slider-Keys die optimistisch sein sollen
        const sliderKeys = [
            'paddingTop', 'paddingBottom', 'paddingLeft', 'paddingRight',
            'paddingTopMobile', 'paddingBottomMobile', 'paddingLeftMobile', 'paddingRightMobile',
            'logoWidth', 'logoWidthMobile',
            'sloganFontSize', 'sloganFontSizeMobile',
            'linkFontSize', 'linkFontSizeMobile',
            'socialIconSize', 'socialIconSizeMobile',
            'footerGap', 'footerGapMobile',
            'borderTopWidth',
            'socialIconContainerPadding',
            'socialIconContainerBorderWidth'
        ];
        
        const updatedKeys = Object.keys(updates);
        const hasSliderValue = updatedKeys.some(key => sliderKeys.includes(key));
        
        if (hasSliderValue) {
            // ⚡ SLIDER VALUES: Optimistic Local State + Debounced Parent
            const localUpdates = {};
            sliderKeys.forEach(key => {
                if (updates[key] !== undefined) {
                    localUpdates[key] = updates[key];
                }
            });
            
            console.log('⚡ [OPTIMISTIC] Updating local styles:', localUpdates);
            setLocalStyles(prev => ({ ...prev, ...localUpdates }));
            
            // Debounced Parent Update (nach 300ms)
            console.log('💾 [DEBOUNCED] Scheduling Firebase update...');
            debouncedParentUpdate({ ...element, ...updates });
        } else {
            // ✅ NON-SLIDER VALUES (Texts, URLs, Colors, Layout): Sofort speichern
            console.log('✅ [INSTANT] Non-slider value, saving immediately:', updates);
            if (onUpdate) {
                onUpdate({ ...element, ...updates });
            }
        }
    };

    // ⚡ OPTIMISTIC ELEMENT: Merge element mit localStyles für Toolbar
    const optimisticElement = {
        ...element,
        ...localStyles
    };

    // ✅ Active Padding (OPTIMISTIC)
    const activePaddingTop = isMobileLayout 
        ? (localStyles.paddingTopMobile ?? element.footerPaddingTopMobile ?? localStyles.paddingTop ?? element.footerPaddingTop ?? 60)
        : (localStyles.paddingTop ?? element.footerPaddingTop ?? 80);
    const activePaddingBottom = isMobileLayout 
        ? (localStyles.paddingBottomMobile ?? element.footerPaddingBottomMobile ?? localStyles.paddingBottom ?? element.footerPaddingBottom ?? 60)
        : (localStyles.paddingBottom ?? element.footerPaddingBottom ?? 80);
    const activePaddingLeft = isMobileLayout 
        ? (localStyles.paddingLeftMobile ?? element.footerPaddingLeftMobile ?? localStyles.paddingLeft ?? element.footerPaddingLeft ?? 40)
        : (localStyles.paddingLeft ?? element.footerPaddingLeft ?? 60);
    const activePaddingRight = isMobileLayout 
        ? (localStyles.paddingRightMobile ?? element.footerPaddingRightMobile ?? localStyles.paddingRight ?? element.footerPaddingRight ?? 40)
        : (localStyles.paddingRight ?? element.footerPaddingRight ?? 60);

    // ✅ Active Values (OPTIMISTIC)
    const activeLogoWidth = isMobileLayout
        ? (localStyles.logoWidthMobile ?? element.logoWidthMobile ?? localStyles.logoWidth ?? element.logoWidth ?? 120)
        : (localStyles.logoWidth ?? element.logoWidth ?? 150);
    const activeSloganFontSize = isMobileLayout
        ? (localStyles.sloganFontSizeMobile ?? element.sloganFontSizeMobile ?? localStyles.sloganFontSize ?? element.sloganFontSize ?? 14)
        : (localStyles.sloganFontSize ?? element.sloganFontSize ?? 16);
    const activeLinkFontSize = isMobileLayout
        ? (localStyles.linkFontSizeMobile ?? element.linkFontSizeMobile ?? localStyles.linkFontSize ?? element.linkFontSize ?? 14)
        : (localStyles.linkFontSize ?? element.linkFontSize ?? 15);
    const activeSocialIconSize = isMobileLayout
        ? (localStyles.socialIconSizeMobile ?? element.socialIconSizeMobile ?? localStyles.socialIconSize ?? element.socialIconSize ?? 20)
        : (localStyles.socialIconSize ?? element.socialIconSize ?? 24);
    const activeGap = isMobileLayout
        ? (localStyles.gapMobile ?? element.footerGapMobile ?? localStyles.gap ?? element.footerGap ?? 20)
        : (localStyles.gap ?? element.footerGap ?? 30);

    // ✅ Footer Data
    const logoUrl = element.logoUrl || '';
    const sloganText = element.sloganText || 'Your Slogan Here';
    const socialLinks = element.socialLinks || [];
    const navigationLinks = element.navigationLinks || [];
    
    // ✅ Social Icon Container Settings (OPTIMISTIC)
    const socialIconContainerEnabled = element.socialIconContainerEnabled || false;
    const socialIconContainerShape = element.socialIconContainerShape || 'circle';
    const socialIconContainerBg = element.socialIconContainerBg || 'transparent';
    const socialIconContainerPadding = localStyles.socialIconContainerPadding ?? element.socialIconContainerPadding ?? 12;
    const socialIconContainerBorderEnabled = element.socialIconContainerBorderEnabled || false;
    const socialIconContainerBorderWidth = localStyles.socialIconContainerBorderWidth ?? element.socialIconContainerBorderWidth ?? 2;
    const socialIconContainerBorderColor = element.socialIconContainerBorderColor || '#ffffff';
    
    // ✅ Style Settings
    const backgroundColor = element.footerBackgroundColor || colors.color1 || '#1a1a1a';
    const textColor = element.footerTextColor || '#ffffff';
    const linkColor = element.footerLinkColor || '#a0a0a0';
    const linkHoverColor = element.footerLinkHoverColor || '#ffffff';
    const borderTopEnabled = element.footerBorderTopEnabled || false;
    const borderTopColor = element.footerBorderTopColor || '#333333';
    const borderTopWidth = localStyles.borderTopWidth ?? element.borderTopWidth ?? 1;
    
    // ✅ Gradient Settings
    const gradientEnabled = element.footerGradientEnabled || false;
    const gradientColor1 = element.footerGradientColor1 || '#667eea';
    const gradientColor2 = element.footerGradientColor2 || '#764ba2';
    
    // ✅ Premium Styling Settings
    const maxWidth = element.footerMaxWidth || 1400;
    const borderRadius = element.footerBorderRadius || 0;
    const shadowEnabled = element.footerShadowEnabled || false;
    const shadowIntensity = element.footerShadowIntensity || 15;
    const shadowOpacity = element.footerShadowOpacity || 20;
    const backgroundOpacity = element.footerBackgroundOpacity ?? 100;
    const dividerEnabled = element.footerDividerEnabled || false;
    const linkHoverAnimation = element.footerLinkHoverAnimation ?? true;
    
    // ✅ Layout Settings with Mobile Support
    const layoutTemplate = isMobileLayout 
        ? (element.footerLayoutMobile || 'stacked')
        : (element.footerLayout || 'centered');
    
    // ✅ Helper: Convert hex color to rgba
    const hexToRgba = (hex, opacity) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
    };
    
    // ✅ Background with Opacity Support
    const getBackground = () => {
        if (gradientEnabled) {
            return `linear-gradient(135deg, ${gradientColor1} 0%, ${gradientColor2} 100%)`;
        }
        if (backgroundOpacity < 100) {
            return hexToRgba(backgroundColor, backgroundOpacity);
        }
        return backgroundColor;
    };
    
    // ✅ Box Shadow
    const getBoxShadow = () => {
        if (!shadowEnabled) return 'none';
        return `0 ${shadowIntensity}px ${shadowIntensity * 2}px rgba(0, 0, 0, ${shadowOpacity / 100})`;
    };

    // ✅ Footer Container Styles
    const footerContainerStyle = {
        width: '100%',
        background: getBackground(),
        color: textColor,
        paddingTop: appendPx(activePaddingTop),
        paddingBottom: appendPx(activePaddingBottom),
        paddingLeft: appendPx(activePaddingLeft),
        paddingRight: appendPx(activePaddingRight),
        borderTop: borderTopEnabled ? `${borderTopWidth}px solid ${borderTopColor}` : 'none',
        borderRadius: appendPx(borderRadius),
        boxShadow: getBoxShadow(),
        cursor: isEditMode ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: borderRadius > 0 ? 'hidden' : 'visible'
    };
    
    // ✅ Inner Container für Max Width
    const innerContainerStyle = {
        maxWidth: appendPx(maxWidth),
        margin: '0 auto',
        width: '100%'
    };
    
    // ✅ Divider Style
    const dividerStyle = dividerEnabled ? {
        width: '100%',
        height: '1px',
        background: `rgba(${textColor === '#ffffff' ? '255, 255, 255' : '0, 0, 0'}, 0.1)`,
        margin: `${appendPx(activeGap / 2)} 0`
    } : null;
    
    // ✅ Helper Function: Render Social Icon mit optionalem Container
    const renderSocialIcon = (social, index) => {
        const iconElement = (
            <i className={`fab ${social.icon || 'fa-link'}`}></i>
        );
        
        if (!socialIconContainerEnabled) {
            // ✅ Ohne Container: Nur Icon
            return (
                <a 
                    key={index}
                    href={social.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-social-link"
                    style={{ 
                        color: linkColor,
                        fontSize: appendPx(activeSocialIconSize),
                        transition: 'color 0.2s',
                        textDecoration: 'none'
                    }}
                >
                    {iconElement}
                </a>
            );
        }
        
        // ✅ Mit Container: Icon in Container
        const getBorderRadius = () => {
            switch (socialIconContainerShape) {
                case 'circle':
                    return '50%';
                case 'rounded':
                    return '12px';
                case 'square':
                    return '0px';
                default:
                    return '50%';
            }
        };
        
        return (
            <a 
                key={index}
                href={social.url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-link with-container"
                style={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: linkColor,
                    fontSize: appendPx(activeSocialIconSize),
                    backgroundColor: socialIconContainerBg,
                    padding: appendPx(socialIconContainerPadding),
                    borderRadius: getBorderRadius(),
                    border: socialIconContainerBorderEnabled 
                        ? `${socialIconContainerBorderWidth}px solid ${socialIconContainerBorderColor}` 
                        : 'none',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none'
                }}
            >
                {iconElement}
            </a>
        );
    };

    // ✅ Render Different Layout Templates
    const renderFooterContent = () => {
        switch (layoutTemplate) {
            case 'centered':
                return renderCenteredLayout();
            case '3-column':
                return render3ColumnLayout();
            case '2-column':
                return render2ColumnLayout();
            case 'stacked':
                return renderStackedLayout();
            case 'split':
                return renderSplitLayout();
            case 'asymmetric':
                return renderAsymmetricLayout();
            default:
                return renderCenteredLayout();
        }
    };

    // ✅ CENTERED LAYOUT - Logo + Slogan + Socials + Links (alles zentriert)
    const renderCenteredLayout = () => (
        <div className="footer-centered-layout" style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: appendPx(activeGap),
            textAlign: 'center'
        }}>
            {/* Logo */}
            {logoUrl && (
                <img 
                    src={logoUrl} 
                    alt="Logo" 
                    className="footer-logo"
                    style={{ 
                        width: appendPx(activeLogoWidth), 
                        height: 'auto',
                        objectFit: 'contain'
                    }}
                />
            )}
            
            {/* Slogan */}
            {sloganText && (
                <p className="footer-slogan" style={{ 
                    fontSize: appendPx(activeSloganFontSize),
                    color: textColor,
                    margin: 0,
                    fontFamily: element.sloganFontFamily || fonts.globalSmallFont || 'inherit',
                    maxWidth: '600px'
                }}>
                    {sloganText}
                </p>
            )}
            
            {/* Divider */}
            {dividerStyle && (sloganText || logoUrl) && (socialLinks.length > 0 || navigationLinks.length > 0) && (
                <div style={dividerStyle}></div>
            )}
            
            {/* Social Links */}
            {socialLinks.length > 0 && (
                <div className="footer-socials" style={{ 
                    display: 'flex', 
                    gap: '16px',
                    alignItems: 'center'
                }}>
                    {socialLinks.map((social, index) => renderSocialIcon(social, index))}
                </div>
            )}
            
            {/* Divider */}
            {dividerStyle && socialLinks.length > 0 && navigationLinks.length > 0 && (
                <div style={dividerStyle}></div>
            )}
            
            {/* Navigation Links */}
            {navigationLinks.length > 0 && (
                <nav className="footer-nav" style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap',
                    gap: '24px',
                    justifyContent: 'center'
                }}>
                    {navigationLinks.map((link, index) => (
                        <a 
                            key={index}
                            href={link.url || '#'}
                            className={`footer-nav-link ${linkHoverAnimation ? 'with-hover-animation' : ''}`}
                            style={{ 
                                color: linkColor,
                                fontSize: appendPx(activeLinkFontSize),
                                textDecoration: 'none',
                                transition: 'color 0.2s',
                                fontFamily: element.linkFontFamily || fonts.globalSmallFont || 'inherit'
                            }}
                        >
                            {link.label || 'Link'}
                        </a>
                    ))}
                </nav>
            )}
        </div>
    );

    // ✅ 3-COLUMN LAYOUT - Logo Left, Links Center, Socials Right
    const render3ColumnLayout = () => (
        <div className="footer-3column-layout" style={{ 
            display: 'grid',
            gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 2fr 1fr',
            gap: appendPx(activeGap),
            alignItems: 'center'
        }}>
            {/* Column 1: Logo + Slogan */}
            <div className="footer-column footer-column-left" style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '12px',
                alignItems: isMobileLayout ? 'center' : 'flex-start'
            }}>
                {logoUrl && (
                    <img 
                        src={logoUrl} 
                        alt="Logo" 
                        className="footer-logo"
                        style={{ 
                            width: appendPx(activeLogoWidth), 
                            height: 'auto',
                            objectFit: 'contain'
                        }}
                    />
                )}
                {sloganText && (
                    <p className="footer-slogan" style={{ 
                        fontSize: appendPx(activeSloganFontSize),
                        color: textColor,
                        margin: 0,
                        fontFamily: element.sloganFontFamily || fonts.globalSmallFont || 'inherit'
                    }}>
                        {sloganText}
                    </p>
                )}
            </div>
            
            {/* Column 2: Navigation Links */}
            <nav className="footer-column footer-column-center" style={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: '20px',
                justifyContent: isMobileLayout ? 'center' : 'center'
            }}>
                {navigationLinks.map((link, index) => (
                    <a 
                        key={index}
                        href={link.url || '#'}
                        className="footer-nav-link"
                        style={{ 
                            color: linkColor,
                            fontSize: appendPx(activeLinkFontSize),
                            textDecoration: 'none',
                            transition: 'color 0.2s',
                            fontFamily: fonts.globalSmallFont || 'inherit'
                        }}
                    >
                        {link.label || 'Link'}
                    </a>
                ))}
            </nav>
            
            {/* Column 3: Social Links */}
            <div className="footer-column footer-column-right" style={{ 
                display: 'flex', 
                gap: '16px',
                justifyContent: isMobileLayout ? 'center' : 'flex-end'
            }}>
                {socialLinks.map((social, index) => renderSocialIcon(social, index))}
            </div>
        </div>
    );

    // ✅ 2-COLUMN LAYOUT - Logo + Slogan Left, Links + Socials Right
    const render2ColumnLayout = () => (
        <div className="footer-2column-layout" style={{ 
            display: 'grid',
            gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 1fr',
            gap: appendPx(activeGap),
            alignItems: 'center'
        }}>
            {/* Column 1: Logo + Slogan */}
            <div className="footer-column footer-column-left" style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '12px',
                alignItems: isMobileLayout ? 'center' : 'flex-start'
            }}>
                {logoUrl && (
                    <img 
                        src={logoUrl} 
                        alt="Logo" 
                        className="footer-logo"
                        style={{ 
                            width: appendPx(activeLogoWidth), 
                            height: 'auto',
                            objectFit: 'contain'
                        }}
                    />
                )}
                {sloganText && (
                    <p className="footer-slogan" style={{ 
                        fontSize: appendPx(activeSloganFontSize),
                        color: textColor,
                        margin: 0,
                        fontFamily: element.sloganFontFamily || fonts.globalSmallFont || 'inherit'
                    }}>
                        {sloganText}
                    </p>
                )}
            </div>
            
            {/* Column 2: Links + Socials */}
            <div className="footer-column footer-column-right" style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '20px',
                alignItems: isMobileLayout ? 'center' : 'flex-end'
            }}>
                {/* Navigation Links */}
                {navigationLinks.length > 0 && (
                    <nav className="footer-nav" style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap',
                        gap: '20px',
                        justifyContent: isMobileLayout ? 'center' : 'flex-end'
                    }}>
                        {navigationLinks.map((link, index) => (
                            <a 
                                key={index}
                                href={link.url || '#'}
                                className={`footer-nav-link ${linkHoverAnimation ? 'with-hover-animation' : ''}`}
                                style={{ 
                                    color: linkColor,
                                    fontSize: appendPx(activeLinkFontSize),
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    fontFamily: element.linkFontFamily || fonts.globalSmallFont || 'inherit'
                                }}
                            >
                                {link.label || 'Link'}
                            </a>
                        ))}
                    </nav>
                )}
                
                {/* Social Links */}
                {socialLinks.length > 0 && (
                    <div className="footer-socials" style={{ 
                        display: 'flex', 
                        gap: '16px'
                    }}>
                        {socialLinks.map((social, index) => renderSocialIcon(social, index))}
                    </div>
                )}
            </div>
        </div>
    );

    // ✅ STACKED LAYOUT - Alles untereinander (mobile-first)
    const renderStackedLayout = () => (
        <div className="footer-stacked-layout" style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: appendPx(activeGap),
            alignItems: 'flex-start'
        }}>
            {/* Logo + Slogan */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {logoUrl && (
                    <img 
                        src={logoUrl} 
                        alt="Logo" 
                        className="footer-logo"
                        style={{ 
                            width: appendPx(activeLogoWidth), 
                            height: 'auto',
                            objectFit: 'contain'
                        }}
                    />
                )}
                {sloganText && (
                    <p className="footer-slogan" style={{ 
                        fontSize: appendPx(activeSloganFontSize),
                        color: textColor,
                        margin: 0,
                        fontFamily: element.sloganFontFamily || fonts.globalSmallFont || 'inherit'
                    }}>
                        {sloganText}
                    </p>
                )}
            </div>
            
            {/* Divider */}
            {dividerStyle && navigationLinks.length > 0 && (
                <div style={dividerStyle}></div>
            )}
            
            {/* Navigation Links */}
            {navigationLinks.length > 0 && (
                <nav className="footer-nav" style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '12px'
                }}>
                    {navigationLinks.map((link, index) => (
                        <a 
                            key={index}
                            href={link.url || '#'}
                            className={`footer-nav-link ${linkHoverAnimation ? 'with-hover-animation' : ''}`}
                            style={{ 
                                color: linkColor,
                                fontSize: appendPx(activeLinkFontSize),
                                textDecoration: 'none',
                                transition: 'color 0.2s',
                                fontFamily: element.linkFontFamily || fonts.globalSmallFont || 'inherit'
                            }}
                        >
                            {link.label || 'Link'}
                        </a>
                    ))}
                </nav>
            )}
            
            {/* Divider */}
            {dividerStyle && navigationLinks.length > 0 && socialLinks.length > 0 && (
                <div style={dividerStyle}></div>
            )}
            
            {/* Social Links */}
            {socialLinks.length > 0 && (
                <div className="footer-socials" style={{ 
                    display: 'flex', 
                    gap: '16px'
                }}>
                    {socialLinks.map((social, index) => renderSocialIcon(social, index))}
                </div>
            )}
        </div>
    );
    
    // ✅ SPLIT LAYOUT - 60/40 Split (Logo/Slogan links, Social/Links rechts)
    const renderSplitLayout = () => (
        <div className="footer-split-layout">
            {/* Left Side - Logo + Slogan */}
            <div className="footer-split-left">
                {logoUrl && (
                    <img 
                        src={logoUrl} 
                        alt="Logo" 
                        className="footer-logo"
                        style={{ 
                            width: appendPx(activeLogoWidth), 
                            height: 'auto',
                            objectFit: 'contain'
                        }}
                    />
                )}
                {sloganText && (
                    <p className="footer-slogan" style={{ 
                        fontSize: appendPx(activeSloganFontSize),
                        color: textColor,
                        margin: 0,
                        maxWidth: '600px',
                        fontFamily: element.sloganFontFamily || fonts.globalSmallFont || 'inherit'
                    }}>
                        {sloganText}
                    </p>
                )}
            </div>
            
            {/* Right Side - Social + Links */}
            <div className="footer-split-right">
                {socialLinks.length > 0 && (
                    <>
                        {dividerStyle && <div style={dividerStyle}></div>}
                        <div className="footer-socials" style={{ 
                            display: 'flex', 
                            gap: '16px'
                        }}>
                            {socialLinks.map((social, index) => renderSocialIcon(social, index))}
                        </div>
                    </>
                )}
                
                {navigationLinks.length > 0 && (
                    <>
                        {dividerStyle && <div style={dividerStyle}></div>}
                        <nav className="footer-nav" style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '12px',
                            alignItems: 'flex-end'
                        }}>
                            {navigationLinks.map((link, index) => (
                                <a 
                                    key={index}
                                    href={link.url || '#'}
                                    className={`footer-nav-link ${linkHoverAnimation ? 'with-hover-animation' : ''}`}
                                    style={{ 
                                        color: linkColor,
                                        fontSize: appendPx(activeLinkFontSize),
                                        textDecoration: 'none',
                                        transition: 'color 0.2s',
                                        fontFamily: element.linkFontFamily || fonts.globalSmallFont || 'inherit'
                                    }}
                                >
                                    {link.label || 'Link'}
                                </a>
                            ))}
                        </nav>
                    </>
                )}
            </div>
        </div>
    );
    
    // ✅ ASYMMETRIC LAYOUT - 30/70 Split (Logo links, Rest rechts)
    const renderAsymmetricLayout = () => (
        <div className="footer-asymmetric-layout">
            {/* Left Side - Logo Only */}
            <div className="footer-asymmetric-left">
                {logoUrl && (
                    <img 
                        src={logoUrl} 
                        alt="Logo" 
                        className="footer-logo"
                        style={{ 
                            width: appendPx(activeLogoWidth), 
                            height: 'auto',
                            objectFit: 'contain'
                        }}
                    />
                )}
            </div>
            
            {/* Right Side - Slogan + Social + Links */}
            <div className="footer-asymmetric-right">
                {sloganText && (
                    <p className="footer-slogan" style={{ 
                        fontSize: appendPx(activeSloganFontSize),
                        color: textColor,
                        margin: 0,
                        fontFamily: element.sloganFontFamily || fonts.globalSmallFont || 'inherit'
                    }}>
                        {sloganText}
                    </p>
                )}
                
                {dividerStyle && <div style={dividerStyle}></div>}
                
                {socialLinks.length > 0 && (
                    <div className="footer-socials" style={{ 
                        display: 'flex', 
                        gap: '16px',
                        justifyContent: 'flex-start'
                    }}>
                        {socialLinks.map((social, index) => renderSocialIcon(social, index))}
                    </div>
                )}
                
                {dividerStyle && navigationLinks.length > 0 && <div style={dividerStyle}></div>}
                
                {navigationLinks.length > 0 && (
                    <nav className="footer-nav" style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap',
                        gap: '24px',
                        justifyContent: 'flex-start'
                    }}>
                        {navigationLinks.map((link, index) => (
                            <a 
                                key={index}
                                href={link.url || '#'}
                                className={`footer-nav-link ${linkHoverAnimation ? 'with-hover-animation' : ''}`}
                                style={{ 
                                    color: linkColor,
                                    fontSize: appendPx(activeLinkFontSize),
                                    textDecoration: 'none',
                                    transition: 'color 0.2s',
                                    fontFamily: element.linkFontFamily || fonts.globalSmallFont || 'inherit'
                                }}
                            >
                                {link.label || 'Link'}
                            </a>
                        ))}
                    </nav>
                )}
            </div>
        </div>
    );

    return (
        <div 
            ref={footerRef}
            className={`universal-footer-container ${isEditMode ? 'edit-mode' : ''}`}
            style={footerContainerStyle}
            onClick={handleClick}
        >
            {/* Inner Container für Max Width */}
            <div style={innerContainerStyle}>
                {renderFooterContent()}
            </div>

            {/* 🎨 Floating Toolbar - Nur wenn aktiv editiert wird */}
            {isEditMode && showToolbar && isCurrentlyEditing && (
                <FooterFloatingToolbar
                    element={optimisticElement}
                    onClose={handleToolbarClose}
                    onStyleChange={handleStyleChange}
                    colors={colors}
                    fonts={fonts}
                    anchorRef={footerRef}
                    isMobileLayout={isMobileLayout}
                />
            )}
        </div>
    );
}

export default UniversalFooterElement;

